import { apiErrorCode, apiErrorStatus } from '@fixit/api-client';
import { bozpTrainingControllerReportClientV1 } from '@fixit/api-client/bozp-training';
// Single model files, never the `@fixit/api-client/models` barrel (see telemetry.ts).
import type { BozpClientReportDto } from '@fixit/api-client/models/bozpClientReportDto';
import { BozpClientReportKind } from '@fixit/api-client/models/bozpClientReportKind';
import { getBozpToken } from '@/api/bozp-token';

/**
 * The player's side of `POST /bozp/client-reports` (fixit/4329): breadcrumbs
 * and failures the server cannot see on its own. A device that stalled on the
 * landing on 2026-09-28 left one training GET in the API log and nothing else;
 * with these reports it leaves the landing, the failed session open, or the
 * uncaught error, each with its user agent (read server-side).
 *
 * Fire-and-forget and never-throwing: a report that cannot be delivered is
 * dropped, it never blocks or breaks the player. The scoped token is the only
 * credential, so a report made before the handoff exchange waits in a small
 * in-memory buffer until {@link flushClientReports}. Bounded per page load:
 * the landing breadcrumb once, failures up to {@link MAX_FAILURE_REPORTS}.
 */

/** Mirrors `BOZP_CLIENT_REPORT_MESSAGE_MAX` on the API (longer is refused there). */
export const CLIENT_REPORT_MESSAGE_MAX = 500;
const ROUTE_MAX = 200;
const ERROR_CODE_MAX = 100;
const ROUTE_PATTERN = /^\/[A-Za-z0-9/_.-]*$/;
const ERROR_CODE_PATTERN = /^[A-Za-z0-9_.:-]+$/;
const MAX_PENDING = 10;
export const MAX_FAILURE_REPORTS = 10;
/** Browser noise with no bearing on the player. */
const IGNORED_MESSAGES = [/ResizeObserver loop/];

let pending: BozpClientReportDto[] = [];
const sentOnce = new Set<string>();
const sentFailures = new Set<string>();

function deliver(report: BozpClientReportDto): void {
  if (!getBozpToken()) {
    if (pending.length < MAX_PENDING) pending.push(report);
    return;
  }
  try {
    bozpTrainingControllerReportClientV1(report).catch(() => {
      // Undeliverable (offline, expired token, throttled): dropped by design,
      // a report must never surface as an error of its own.
    });
  } catch {
    // A synchronous throw from the client is dropped the same way.
  }
}

/** Send what was reported before the scoped token existed (call after the exchange). */
export function flushClientReports(): void {
  if (!getBozpToken()) return;
  const queued = pending;
  pending = [];
  for (const report of queued) deliver(report);
}

/** The player route, path only: the query carries the one-time handoff code. */
function currentRoute(): string | undefined {
  const path = typeof window === 'undefined' ? '' : window.location.pathname;
  return path.length <= ROUTE_MAX && ROUTE_PATTERN.test(path) ? path : undefined;
}

/** Never a credential in a log line: handoff codes and JWTs are masked. */
function scrub(text: string): string {
  return text
    .replace(/handoff=[^&\s]+/g, 'handoff=[redacted]')
    .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]+/g, '[jwt]');
}

export function errorMessageOf(error: unknown): string {
  let text: string;
  if (error instanceof Error) text = `${error.name}: ${error.message}`;
  else if (typeof error === 'string') text = error;
  else if (
    error &&
    typeof error === 'object' &&
    typeof (error as { message?: unknown }).message === 'string'
  )
    text = (error as { message: string }).message;
  else text = 'Unknown error';
  return scrub(text).slice(0, CLIENT_REPORT_MESSAGE_MAX);
}

function errorCodeOf(error: unknown): string | undefined {
  const code = apiErrorCode(error) ?? (error instanceof Error ? error.name : undefined);
  return code && code.length <= ERROR_CODE_MAX && ERROR_CODE_PATTERN.test(code) ? code : undefined;
}

function httpStatusOf(error: unknown): number | undefined {
  const status = apiErrorStatus(error);
  return status !== undefined && Number.isInteger(status) && status >= 0 && status <= 599
    ? status
    : undefined;
}

function reportFailure(
  kind: BozpClientReportKind,
  error: unknown,
  opts: { hazardProfileId?: string; stage?: string },
): void {
  const message = errorMessageOf(error);
  if (IGNORED_MESSAGES.some((pattern) => pattern.test(message))) return;
  const text = (opts.stage ? `[${opts.stage}] ${message}` : message).slice(
    0,
    CLIENT_REPORT_MESSAGE_MAX,
  );
  const key = `${kind}|${opts.hazardProfileId ?? ''}|${text}`;
  if (sentFailures.size >= MAX_FAILURE_REPORTS || sentFailures.has(key)) return;
  sentFailures.add(key);
  deliver({
    kind,
    hazardProfileId: opts.hazardProfileId,
    route: currentRoute(),
    httpStatus: httpStatusOf(error),
    errorCode: errorCodeOf(error),
    message: text,
  });
}

/** Breadcrumb: the training list rendered on this device (once per page load). */
export function reportLandingLoaded(summary: { trainings: number; loaded: number }): void {
  if (sentOnce.has(BozpClientReportKind.LANDING_LOADED)) return;
  sentOnce.add(BozpClientReportKind.LANDING_LOADED);
  deliver({
    kind: BozpClientReportKind.LANDING_LOADED,
    route: currentRoute(),
    message: `trainings ${summary.loaded}/${summary.trainings} loaded`,
  });
}

/** `POST /bozp/sessions` failed on the device, network drops included. */
export function reportSessionOpenFailed(hazardProfileId: string, error: unknown): void {
  reportFailure(BozpClientReportKind.SESSION_OPEN_FAILED, error, { hazardProfileId });
}

/** An error the player caught (with the step it broke) or an uncaught one. */
export function reportClientError(
  error: unknown,
  opts: { hazardProfileId?: string; stage?: string } = {},
): void {
  reportFailure(BozpClientReportKind.CLIENT_ERROR, error, opts);
}

/**
 * Report uncaught errors and unhandled rejections of the whole player. Returns
 * the cleanup; the handlers only report, they never change what the browser
 * does with the error.
 */
export function installClientErrorReporting(target: Window = window): () => void {
  const onError = (event: ErrorEvent) => {
    reportClientError(event.error ?? event.message, { stage: 'uncaught' });
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    reportClientError(event.reason, { stage: 'unhandled_rejection' });
  };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}
